// Setup → Agency's own client panel (finance-real, the user 2026-10-22: "the agency's own profile is set in the global
// setup page"), beside Team claims: which client is the agency's own (TD-105), the one whose jobs record what freelancers
// do for the agency, with no quote or invoice. It replaces the client page header's ⋮ item and main's OwnClientModal,
// and writes main's route the same way (PUT /own-client, useSetOwnClient). A WorkspacePanel like every Setup panel
// (DD-002, DD-047), whose one action opens the drawer picking it (DD-005, DD-051), `modal=own-client`. The server refuses
// a client already invoiced: the refusal shows in the footer and the drawer stays open.
import { useState } from "react";
import { EditIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { FormField } from "@/components/ui/FormField";
import { OptionDropdown, type DropdownOption } from "@/components/ui/OptionDropdown";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { ModalPending } from "../components/ModalPending";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { ClientSummary, OwnClient } from "../types";
import { closeUnlessPending } from "../utils/mutations";
import { useOwnClientCard } from "./hooks/useOwnClientCard";
import { useOwnClientDrawer, type OwnClientDrawerModel } from "./hooks/useOwnClientDrawer";

const LABEL = "Agency's own client";
const CLIENT = ENTITY_CONFIG.client;
const jobsWord = ENTITY_CONFIG.contract.plural.toLowerCase();
const quoteWord = ENTITY_CONFIG.quote.label.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();
const MEANING = `Its ${jobsWord} record what freelancers do for the agency: work pay, never revenue, with no ${quoteWord} and no ${invoiceWord}. Its ad spend is an expense.`;
const NONE = "none";

export function OwnClientCard({ onEdit }: { onEdit: () => void }) {
  const { own } = useOwnClientCard();
  const name = own.data?.clientName ?? null;
  return (
    <WorkspacePanel
      title={LABEL}
      collapsed={false}
      loading={own.isLoading}
      className="h-full"
      actions={<PanelHeaderAction label="Change" icon={<EditIcon className="w-4 h-4" />} onClick={onEdit} disabled={!own.data} />}
    >
      <div className="space-y-2 text-sm">
        {own.isError && <p className="text-red-600">Could not load the agency's own client.</p>}
        {own.data && (
          <div>
            <p className="text-xs text-gray-500">{`The ${CLIENT.label.toLowerCase()} that is the agency itself`}</p>
            <p className={`mt-1 text-xl font-semibold ${name ? "text-gray-900" : "text-gray-400"}`}>{name ?? "None set"}</p>
          </div>
        )}
        <p className="text-xs text-gray-500">{MEANING}</p>
      </div>
    </WorkspacePanel>
  );
}

export function OwnClientDrawer({ onClose }: { onClose: () => void }) {
  const { own, clients, save } = useOwnClientDrawer();
  if (!own.data || !clients.data) {
    const failed = own.isError || clients.isError;
    return (
      <ModalPending
        title={LABEL}
        onClose={onClose}
        failure={failed ? "Could not load the agency's own client." : null}
        retrying={own.isFetching || clients.isFetching}
        retry={() => {
          void own.refetch();
          void clients.refetch();
        }}
      />
    );
  }
  return <OwnClientForm current={own.data} clients={clients.data.clients} save={save} onClose={onClose} />;
}

function OwnClientForm({ current, clients, save, onClose }: { current: OwnClient; clients: ClientSummary[]; save: OwnClientDrawerModel["save"]; onClose: () => void }) {
  const send = useSubmitOnce();
  const [picked, setPicked] = useState<string>(current.clientId ?? NONE);
  // An archived client is not offered, unless it is the one set now.
  const options: DropdownOption[] = [
    { value: NONE, label: "None" },
    ...clients
      .filter((c) => c.status !== "ARCHIVED" || c.id === current.clientId)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({ value: c.id, label: c.name })),
  ];
  const changed = picked !== (current.clientId ?? NONE);
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(save.isPending, onClose)}
      title={`Change · ${LABEL}`}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-3">
          {save.error && (
            <Callout tone="red" className="w-full">
              {save.error.message}
            </Callout>
          )}
          <Button disabled={!changed || save.isPending} isLoading={save.isPending} onClick={() => send(() => save.mutateAsync(picked === NONE ? null : picked), () => onClose())}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-gray-700">
        <FormField label={CLIENT.label} hint={`A ${CLIENT.label.toLowerCase()} already invoiced cannot be the agency's own.`}>
          <OptionDropdown value={picked} options={options} onChange={setPicked} variant="input" ariaLabel={LABEL} />
        </FormField>
        <p>{MEANING}</p>
      </div>
    </FinanceDrawer>
  );
}
